/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { animate, baseStyles, event, i18n, I18nService, state, isVisible, onKey, property, querySlot, querySlotAll, reverseAnimation, setAttributes, syncProps, syncPropsForAllItems, } from '@cds/core/internal';
import styles from './navigation.element.scss';
import { getNextFocusElement, getPreviousFocusElement, removeFocus, setFocus, visibleElement, } from './utils/utils.js';
import { AnimationNavigationOpenName } from '../internal/motion/animations/cds-navigation-open.js';
export const CdsNavigationTagName = 'cds-navigation';
/**
 * ```typescript
 * import '@cds/core/navigation/register.js';
 * ```
 *
 * ```html
 *  <cds-navigation>
 *    <cds-navigation-item><a href="/home">Home</cds-navigation-item>
 *    <cds-navigation-item><a href="/account">Account</cds-navigation-item>
 *  </cds-navigation>
 * ```
 *
 * @beta
 * @element cds-navigation
 * @event expandedChange - notify when the user has clicked the navigation expand/collapse button
 * @cssprop --animation-duration
 * @cssprop --animation-easing
 * @cssprop --background
 * @cssprop --collapsed-width
 * @cssprop --expanded-width
 * @cssprop --font-size
 * @cssprop --font-weight
 * @cssprop --letter-spacing
 * @cssprop --line-height
 * @cssprop --nested-padding
 * @cssprop --padding
 * @slot
 * @slot cds-navigation-substart - project content below the navigation toggle button
 * @slot cds-navigation-end - project content below the scrollable section
 */
let CdsNavigation = class CdsNavigation extends LitElement {
    constructor() {
        super(...arguments);
        this.expandedRoot = false;
        this.cdsMotion = 'on';
        /**
         * This is used to sync down the information to this.navigationGroupItems
         *
         * @type { boolean }
         * @protected
         */
        this.groupItem = true;
        /**
         *
         * Vertical navigation elements can be either wide or narrow. Expanded indicates it should be wide.
         * When navigation is wide cds-navigation-start button elements and cds-navigation-item a elements display
         * text. When it is narrow they do not (consumer should provide an icon that stays visible).
      
         * @type {boolean}
         */
        this.expanded = false;
        this.i18n = I18nService.keys.navigation;
    }
    static get styles() {
        return [baseStyles, styles];
    }
    get endTemplate() {
        return this.navigationEnd
            ? html `
          <div class="navigation-end" cds-layout="vertical align:horizontal-stretch">
            <slot name="cds-navigation-end" @slotchange=${this.onItemSlotChange}></slot>
          </div>
        `
            : '';
    }
    get startTemplate() {
        let returnHTML;
        this.rootNavigationStart
            ? (returnHTML = html `
          <div class="navigation-start" cds-layout="vertical align:horizontal-stretch">
            <slot @slotchange="${() => this.onStartItemSlotChange()}" name="navigation-start"></slot>
            <cds-divider class="start-divider"></cds-divider>
          </div>
        `)
            : (returnHTML = '');
        return returnHTML;
    }
    render() {
        return html `<div class="private-host" aria-label="${this.i18n.navigationLabel}" cds-layout="vertical wrap:none">
      ${this.startTemplate}
      <slot name="cds-navigation-substart"></slot>
      <nav class="navigation-body-wrapper">
        <div .ariaActiveDescendant=${this.ariaActiveDescendant} tabindex="0" id="item-container">
          <div class="navigation-body" cds-layout="vertical wrap:none align:horizontal-stretch">
            <slot @slotchange=${this.onItemSlotChange}></slot>
          </div>
        </div>
      </nav>
      ${this.endTemplate}
    </div>`;
    }
    connectedCallback() {
        super.connectedCallback();
        this.role = 'list';
        this.rootNavigationStart?.addEventListener('focus', this.focusRootStart.bind(this));
        this.rootNavigationStart?.addEventListener('blur', this.blurRootStart.bind(this));
        this.rootNavigationStart?.addEventListener('keydown', this.handleRootStartKeys.bind(this));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.rootNavigationStart) {
            this.rootNavigationStart.removeEventListener('click', this.toggle.bind(this));
        }
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        const itemWrapper = this.shadowRoot?.querySelector('#item-container');
        itemWrapper?.addEventListener('focus', this.initItemKeys.bind(this));
        itemWrapper?.addEventListener('keydown', this.handleItemKeys.bind(this));
        itemWrapper?.addEventListener('blur', this.blurItemKeys.bind(this));
    }
    updated(props) {
        super.updated(props);
        if (props.has('expanded')) {
            this.expandedRoot = this.expanded;
        }
        this.updateChildrenProps();
    }
    onStartItemSlotChange() {
        this.onItemSlotChange();
        // This is controlled by the slotchange event on the navigation-start slot
        // Make sure we reattach the click handler for toggle if consumer changes the start element (e.g *ngIf)
        if (this.rootNavigationStart) {
            this.rootNavigationStart.addEventListener('click', this.toggle.bind(this));
        }
    }
    onItemSlotChange() {
        this.updateChildrenProps();
        // set all visible navigation elements to tabindex = -1
        this.allNavigationElements.forEach(item => {
            setAttributes(item, ['tabindex', '-1']);
        });
    }
    updateChildrenProps() {
        if (this.navigationGroupItems) {
            syncPropsForAllItems(Array.from(this.navigationGroupItems), this, { groupItem: true });
        }
        if (this.navigationItemRefs) {
            syncPropsForAllItems(Array.from(this.navigationItemRefs), this, {
                expanded: true,
            });
        }
        if (this.navigationStartRefs) {
            syncPropsForAllItems(Array.from(this.navigationStartRefs), this, {
                expandedRoot: true,
            });
        }
        if (this.rootNavigationStart) {
            syncProps(this.rootNavigationStart, this, {
                expanded: this.expanded,
            });
        }
        if (this.rootNavigationItems.length > 0) {
            syncPropsForAllItems(Array.from(this.rootNavigationItems), this, {
                expanded: this.expanded,
            });
        }
        if (this.navigationGroupRefs.length > 0) {
            syncPropsForAllItems(Array.from(this.navigationGroupRefs), this, {
                layout: true,
            });
        }
    }
    get visibleChildren() {
        return Array.from(this.allNavigationElements).filter(n => isVisible(n));
    }
    toggle() {
        this.expandedChange.emit(!this.expanded);
    }
    get currentActiveItem() {
        return this.visibleChildren.find(c => c.id === this.ariaActiveDescendant);
    }
    blurItemKeys() {
        if (this.currentActiveItem) {
            removeFocus(this.currentActiveItem);
        }
    }
    focusRootStart() {
        if (this.rootNavigationStart) {
            setFocus(this.rootNavigationStart);
        }
    }
    blurRootStart() {
        if (this.rootNavigationStart) {
            removeFocus(this.rootNavigationStart);
        }
    }
    initItemKeys() {
        if (!this.currentActiveItem) {
            setFocus(this.focusableElements[0]);
            this.ariaActiveDescendant = this.focusableElements[0].id;
        }
        else {
            const focusElement = this.currentActiveItem ? this.currentActiveItem : this.allNavigationElements[0];
            setFocus(focusElement);
            this.ariaActiveDescendant = focusElement.id;
        }
    }
    get focusableElements() {
        return Array.from(this.allNavigationElements).filter(visibleElement);
    }
    handleItemKeys(event) {
        onKey('arrow-down', event, () => {
            if (this.currentActiveItem) {
                removeFocus(this.currentActiveItem);
                const next = getNextFocusElement(this.currentActiveItem, this.focusableElements);
                this.ariaActiveDescendant = next.id;
                setFocus(next);
                event.preventDefault();
            }
        });
        onKey('arrow-up', event, () => {
            if (this.currentActiveItem) {
                removeFocus(this.currentActiveItem);
                const previous = getPreviousFocusElement(this.currentActiveItem, this.focusableElements);
                this.ariaActiveDescendant = previous.id;
                setFocus(previous);
                event.preventDefault();
            }
        });
        onKey('arrow-right', event, () => {
            const groupParent = this.currentActiveItem?.closest('cds-navigation-group');
            if (groupParent && !groupParent.expanded) {
                groupParent.expandedChange.emit(!groupParent.expanded);
                return;
            }
        });
        onKey('arrow-left', event, () => {
            const groupParent = this.currentActiveItem?.closest('cds-navigation-group');
            if (this.currentActiveItem?.tagName === 'CDS-NAVIGATION-ITEM' && !!groupParent) {
                const groupStartElement = groupParent?.querySelector('cds-navigation-start');
                removeFocus(this.currentActiveItem);
                this.ariaActiveDescendant = groupStartElement?.id ?? null;
                setFocus(groupStartElement);
                return;
            }
            if (groupParent && groupParent.expanded) {
                groupParent.expandedChange.emit(!groupParent.expanded);
                return;
            }
        });
        onKey('home', event, () => {
            if (this.currentActiveItem) {
                removeFocus(this.currentActiveItem);
                const home = this.focusableElements[0];
                this.ariaActiveDescendant = home.id;
                setFocus(home);
            }
        });
        onKey('end', event, () => {
            if (this.currentActiveItem) {
                removeFocus(this.currentActiveItem);
                const end = this.focusableElements[this.focusableElements.length - 1];
                this.ariaActiveDescendant = end.id;
                setFocus(end);
            }
        });
        onKey('enter', event, () => {
            // focusElement is either an Anchor or a button and we want to click it here
            if (this.currentActiveItem?.focusElement) {
                this.currentActiveItem?.focusElement.click();
            }
        });
        onKey('space', event, () => {
            // focusElement is either an Anchor or a button and we want to click it here
            if (this.currentActiveItem?.focusElement) {
                this.currentActiveItem?.focusElement.click();
            }
        });
    }
    handleRootStartKeys(event) {
        onKey('arrow-right', event, () => {
            if (!this.expanded) {
                this.toggle();
                return;
            }
        });
        onKey('arrow-left', event, () => {
            if (this.expanded) {
                this.toggle();
                return;
            }
        });
    }
};
__decorate([
    property({ type: String })
], CdsNavigation.prototype, "cdsMotion", void 0);
__decorate([
    event()
], CdsNavigation.prototype, "expandedChange", void 0);
__decorate([
    event()
], CdsNavigation.prototype, "cdsMotionChange", void 0);
__decorate([
    state({ type: Boolean })
], CdsNavigation.prototype, "groupItem", void 0);
__decorate([
    property({ type: Boolean })
], CdsNavigation.prototype, "expanded", void 0);
__decorate([
    i18n()
], CdsNavigation.prototype, "i18n", void 0);
__decorate([
    querySlot('[slot="cds-navigation-end"]', { assign: 'cds-navigation-end' })
], CdsNavigation.prototype, "navigationEnd", void 0);
__decorate([
    querySlotAll('cds-navigation-group > cds-navigation-start, cds-navigation-item:not([disabled])')
], CdsNavigation.prototype, "allNavigationElements", void 0);
__decorate([
    querySlotAll('cds-navigation-start')
], CdsNavigation.prototype, "navigationStartRefs", void 0);
__decorate([
    querySlotAll(':scope > cds-divider')
], CdsNavigation.prototype, "rootDividers", void 0);
__decorate([
    querySlotAll(':scope > cds-navigation-group')
], CdsNavigation.prototype, "rootNavigationGroups", void 0);
__decorate([
    querySlotAll(':scope > cds-navigation-item')
], CdsNavigation.prototype, "rootNavigationItems", void 0);
__decorate([
    querySlot(':scope > cds-navigation-start', { assign: 'navigation-start' })
], CdsNavigation.prototype, "rootNavigationStart", void 0);
__decorate([
    querySlotAll(':scope > cds-navigation-group > cds-navigation-item')
], CdsNavigation.prototype, "navigationGroupItems", void 0);
__decorate([
    querySlotAll('cds-navigation-item')
], CdsNavigation.prototype, "navigationItemRefs", void 0);
__decorate([
    querySlotAll('cds-navigation-group')
], CdsNavigation.prototype, "navigationGroupRefs", void 0);
CdsNavigation = __decorate([
    animate({
        expanded: {
            true: AnimationNavigationOpenName,
            false: reverseAnimation(AnimationNavigationOpenName),
        },
    })
], CdsNavigation);
export { CdsNavigation };
